import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { db } from '@otw/models/db'
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore'
import { FormulaRule } from '@otw/models'
import { formulaRuleConverter } from '@otw/models/converters'
import { db as firebaseDb } from 'config/firebase'
import { invalidateFormulaStat } from './useFormulaStats'
interface UseFormulaRulesOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  dontShowUpdates?: boolean
}

interface UseFormulaRulesParams {
  formulaId?: string
  options?: UseFormulaRulesOptions
}

export const useFormulaRules = ({ formulaId, options }: UseFormulaRulesParams) => {
  const { showSnackbar, showSuccess, showError, showWarning, showInfo } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()

  const rulesCollectionRef = formulaId ? db.getFormulaRules(formulaId) : db.formulas

  const queryKey = formulaId ? [formulaId] : ['gray_book_formulas']

  const {
    data: rules,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<FormulaRule[]>({
    queryKey,
    queryFn: async () => {
      const collectionWithConverter = rulesCollectionRef.withConverter(formulaRuleConverter)
      const snapshot = await getDocs(collectionWithConverter)
      const docs = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      }))
      return docs.sort((a, b) => a.order - b.order)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  type AddRuleContext = {
    previousRules: FormulaRule[] | undefined
    optimisticRule: FormulaRule
  }

  const addRule = useMutation<
    FormulaRule,
    Error,
    {
      rule: Omit<FormulaRule, 'id' | 'createdAt' | 'createdBy'>
      customQueryKey?: string[]
    },
    AddRuleContext
  >({
    mutationFn: async ({ rule, customQueryKey }) => {
      const targetCollectionRef = customQueryKey
        ? collection(firebaseDb, customQueryKey[0], ...customQueryKey.slice(1)).withConverter(formulaRuleConverter)
        : rulesCollectionRef.withConverter(formulaRuleConverter)

      const newRule = {
        ...rule,
        createdBy: userInfo?.email || '',
        createdAt: new Date().toISOString(),
      } as FormulaRule

      const docRef = await addDoc(targetCollectionRef, formulaRuleConverter.toFirestore(newRule))

      return {
        ...newRule,
        id: docRef.id,
      } as FormulaRule
    },
    onMutate: async ({ rule, customQueryKey }) => {
      const targetQueryKey = customQueryKey || queryKey
      await queryClient.cancelQueries({ queryKey: targetQueryKey })
      const previousRules = queryClient.getQueryData<FormulaRule[]>(targetQueryKey) || []

      const optimisticRule = {
        ...rule,
        id: `temp-${Date.now()}`,
        createdBy: userInfo?.email || '',
        createdAt: new Date().toISOString(),
      }

      queryClient.setQueryData<FormulaRule[]>(targetQueryKey, old => {
        return [...(old || []), optimisticRule]
      })

      return { previousRules, optimisticRule }
    },
    onError: (err, { customQueryKey }, context) => {
      if (context) {
        queryClient.setQueryData(customQueryKey || queryKey, context.previousRules)
      }
      if (!options?.dontShowUpdates) {
        showError('Error adding rule')
      }
      options?.onError?.(err)
    },
    onSuccess: (result, { customQueryKey }, context) => {
      if (context) {
        queryClient.setQueryData<FormulaRule[]>(customQueryKey || queryKey, old => {
          return (old || []).map(rule => (rule.id === context.optimisticRule.id ? result : rule))
        })
      }

      if (!options?.dontShowUpdates) {
        showSuccess('Rule added successfully')
      }
      // Invalidate formula stats since new rule affects calculations
      if (formulaId) {
        invalidateFormulaStat(queryClient, formulaId)
      }
      options?.onSuccess?.()
    },
    onSettled: (_, __, { customQueryKey }) => {
      if (customQueryKey) {
        queryClient.invalidateQueries({ queryKey: customQueryKey })
      }
    },
  })

  type UpdateRuleContext = {
    previousRules: FormulaRule[]
  }

  const updateRule = useMutation<
    { id: string } & Partial<FormulaRule>,
    Error,
    { id: string; data: Partial<FormulaRule> },
    UpdateRuleContext
  >({
    mutationFn: async ({ id, data }) => {
      const collectionWithConverter = rulesCollectionRef.withConverter(formulaRuleConverter)
      const docRef = doc(collectionWithConverter, id)
      const updateData = formulaRuleConverter.toFirestore({
        ...data,
        modified_by: userInfo?.email,
        modified_at: new Date().toISOString(),
      } as FormulaRule)
      await updateDoc(docRef, updateData)
      return { id, ...data }
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousRules = queryClient.getQueryData<FormulaRule[]>(queryKey) || []

      queryClient.setQueryData<FormulaRule[]>(queryKey, old => {
        if (!old) return []
        return old.map(rule => (rule.id === id ? { ...rule, ...data } : rule))
      })

      return { previousRules }
    },
    onError: (err, variables, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousRules)
      }
      if (!options?.dontShowUpdates) {
        showError('Error updating rule')
      }
      options?.onError?.(err)
    },
    onSuccess: result => {
      if (!options?.dontShowUpdates) {
        showSuccess('Rule updated successfully')
      }
      // Invalidate formula stats since rule change affects calculations
      if (formulaId) {
        invalidateFormulaStat(queryClient, formulaId)
      }
      options?.onSuccess?.()
    },
  })

  type DeleteRuleContext = {
    previousRules: FormulaRule[]
  }

  const deleteRule = useMutation<string, Error, string, DeleteRuleContext>({
    mutationFn: async (id: string) => {
      const collectionWithConverter = rulesCollectionRef.withConverter(formulaRuleConverter)
      const docRef = doc(collectionWithConverter, id)
      await deleteDoc(docRef)
      return id
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey })
      const previousRules = queryClient.getQueryData<FormulaRule[]>(queryKey) || []

      queryClient.setQueryData<FormulaRule[]>(queryKey, old => {
        if (!old) return []
        return old.filter(rule => rule.id !== id)
      })

      return { previousRules }
    },
    onError: (err, id, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousRules)
      }
      if (!options?.dontShowUpdates) {
        showError('Error deleting rule')
      }
      options?.onError?.(err)
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Rule deleted successfully')
      }
      // Invalidate formula stats since rule deletion affects calculations
      if (formulaId) {
        invalidateFormulaStat(queryClient, formulaId)
      }
      options?.onSuccess?.()
    },
  })

  return {
    rules,
    isLoading,
    isError,
    error,
    addRule: (rule: Omit<FormulaRule, 'id' | 'createdAt' | 'createdBy'>, customQueryKey?: string[]) =>
      addRule.mutate({ rule, customQueryKey }),
    updateRule: updateRule.mutate,
    deleteRule: deleteRule.mutate,
    refetchRules: refetch,
  }
}
